import { useState, useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import Button from "../../../components/shared/buttons/button";
import DateSelector from "../../../components/shared/fields/DateSelector";
import GridCard from "../../../components/shared/gridCard/GridCard";
import InfoCard from "../../../components/shared/infocard/InfoCard";
import NoDataFound from "../../../components/shared/noDataFound/NoDataFound";
import { PlusSvg } from "../../../svgFiles/PlusSvg";
import SeparationModal from "../../packing/bagging/SeparationModal";
import useTranslator from "../../../hooks/useTranslator";
import { formatDateAndTime, getCurrentDate, makePriorLotNumber } from "../../../helpers/Utils";
import { PROJECT_DATE_FORMAT } from "../../../helpers/constants";
import {
  useGetSeparationRecordQuery,
  useCreateSeparationRecordMutation,
  useUpdateSeparationRecordMutation,
} from "../../packing/packing/packingApi";
import PackingBandModal from "../../packing/packing/PackingBandModal";
import {
  useGetBrokeragePackingBandRecordQuery,
  useCreateBrokeragePackingBandRecordMutation,
  useUpdateBrokeragePackingBandRecordMutation,
  useGetBrokerageSeparationRecordQuery,
  useCreateBrokerageSeparationRecordMutation,
  useUpdateBrokerageSeparationRecordMutation,
} from "../materialEntered/materialApi";

const Turn2 = () => {
  const { _t } = useTranslator();
  const currentDate = getCurrentDate("YYYY-MM-DD");
  const [date, setDate] = useState(currentDate);
  const [lotNumber, setLotNumber] = useState(makePriorLotNumber(date));

  const [showDaySeparationModal, setShowDaySeparationModal] = useState(false);
  const [showNightSeparationModal, setShowNightSeparationModal] = useState(false);
  const [daySeparationMode, setDaySeparationMode] = useState("add");
  const [nightSeparationMode, setNightSeparationMode] = useState("add");
  const [daySeparationData, setDaySeparationData] = useState(null);
  const [nightSeparationData, setNightSeparationData] = useState(null);

  useEffect(() => { setLotNumber(makePriorLotNumber(date)); }, [date]);

  // Packing Band states
  const [showNightBandModal, setShowNightBandModal] = useState(false);
  const [nightBandMode, setNightBandMode] = useState("add");
  const [nightBandData, setNightBandData] = useState(null);

  const { data: nightBandRecordData } = useGetBrokeragePackingBandRecordQuery({ date, shift: "night", lot_number: lotNumber, data_from: "brokerage" });
  const [createBrokeragePackingBandRecord] = useCreateBrokeragePackingBandRecordMutation();
  const [updateBrokeragePackingBandRecord] = useUpdateBrokeragePackingBandRecordMutation();

  useEffect(() => { setNightBandData(nightBandRecordData?.data?.id ? nightBandRecordData.data : null); }, [nightBandRecordData]);

  const handleNightBandAdd = () => { setShowNightBandModal(true); setNightBandMode(nightBandData?.id ? "edit" : "add"); };

  const handleNightBandSave = async (data) => {
    try {
      const payload = { ...data, data_from: "brokerage" };
      if (nightBandData?.id) await updateBrokeragePackingBandRecord({ id: nightBandData.id, requestBody: payload }).unwrap();
      else await createBrokeragePackingBandRecord(payload).unwrap();
    } catch (error) { console.error("Error saving night band data:", error); }
  };

  const hasNightBandRecord = Boolean(nightBandData?.id);

  const nightBandLogData = nightBandData?.id
    ? [1, 2, 3, 4, 5].map((n) => [
        { label: _t("band", "Band") + ` ${n}`, value: nightBandData[`band_${n}_20lb`] || 0, subvalue: "(20 Pound)" },
        { label: _t("band", "Band") + ` ${n}`, value: nightBandData[`band_${n}_10lb`] || 0, subvalue: "(10 Pound)" },
        { label: _t("band", "Band") + ` ${n}`, value: nightBandData[`band_${n}_people`] || 0, subvalue: `(${_t("people", "People")})` },
      ])
    : [];

  const { data: daySeparationRecordData } = useGetBrokerageSeparationRecordQuery({ date, shift: "day", separation_area: "brokerage", lot_number: lotNumber });
  const { data: nightSeparationRecordData } = useGetBrokerageSeparationRecordQuery({ date, shift: "night", separation_area: "brokerage", lot_number: lotNumber });
  const [createSeparationRecord] = useCreateBrokerageSeparationRecordMutation();
  const [updateSeparationRecord] = useUpdateBrokerageSeparationRecordMutation();

  useEffect(() => { setDaySeparationData(daySeparationRecordData?.data || null); }, [daySeparationRecordData]);
  useEffect(() => { setNightSeparationData(nightSeparationRecordData?.data || null); }, [nightSeparationRecordData]);

  const handleDaySeparationAdd = () => { setShowDaySeparationModal(true); setDaySeparationMode(daySeparationData ? "edit" : "add"); };
  const handleNightSeparationAdd = () => { setShowNightSeparationModal(true); setNightSeparationMode(nightSeparationData ? "edit" : "add"); };

  const handleDaySeparationSave = async (data) => {
    try {
      if (daySeparationData?.id) await updateSeparationRecord({ id: daySeparationData.id, requestBody: data }).unwrap();
      else await createSeparationRecord(data).unwrap();
    } catch (error) { console.error("Error saving day separation data:", error); }
  };

  const handleNightSeparationSave = async (data) => {
    try {
      if (nightSeparationData?.id) await updateSeparationRecord({ id: nightSeparationData.id, requestBody: data }).unwrap();
      else await createSeparationRecord(data).unwrap();
    } catch (error) { console.error("Error saving night separation data:", error); }
  };

  const daySeparationLogData = daySeparationData
    ? [[
        { label: _t("strips_kg", "Strips KG"), value: daySeparationData.strips_kg || 0 },
        { label: _t("cheeks_kg", "Cheeks KG"), value: daySeparationData.cheeks_kg || 0 },
        { label: _t("number_of_people_separating", "Number of people Separating"), value: daySeparationData.total_people || 0 },
      ]]
    : [];

  const nightSeparationLogData = nightSeparationData
    ? [[
        { label: _t("strips_kg", "Strips KG"), value: nightSeparationData.strips_kg || 0 },
        { label: _t("cheeks_kg", "Cheeks KG"), value: nightSeparationData.cheeks_kg || 0 },
        { label: _t("number_of_people_separating", "Number of people Separating"), value: nightSeparationData.total_people || 0 },
      ]]
    : [];

  const isToday = date === currentDate;

  return (
    <div>
      <SeparationModal show={showDaySeparationModal} setShow={setShowDaySeparationModal} record={daySeparationData} mode={daySeparationMode} onSave={handleDaySeparationSave} date={date} lotNumber={lotNumber} shift="day" separationArea="brokerage" />
      <SeparationModal show={showNightSeparationModal} setShow={setShowNightSeparationModal} record={nightSeparationData} mode={nightSeparationMode} onSave={handleNightSeparationSave} date={date} lotNumber={lotNumber} shift="night" separationArea="brokerage" />
      <PackingBandModal show={showNightBandModal} setShow={setShowNightBandModal} record={hasNightBandRecord ? nightBandData : null} mode={nightBandMode} onSave={handleNightBandSave} date={date} lotNumber={lotNumber} shift="night" />

      <div className="overview cmn_card mb-3">
        <div className="overview_header d-flex gap-2 align-items-center">
          <h6 className="mb-0 flex-grow-1">{_t("turn_2", "Turn 2")}</h6>
          <div className="d-flex gap-2 align-items-center">
            <h6 className="mb-0">{_t("batch_code", "Batch Code")}</h6>
            <Button label={`#${lotNumber ?? 0}`} size="small" className="borderRedBtn" />
            <DateSelector setDate={setDate} />
          </div>
        </div>
      </div>

      <div className="my-3">
        <div className="gap-2 overviewcard">
          <Row>
            <Col lg={6}>
              <div className="dash_card h-100">
                <div className="d-flex justify-content-between align-items-center dash_header">
                  <div className="packing-card-header-content">
                    <h3 className="mb-0">{_t("day_separation", "Day Separation")}</h3>
                    <span className="shift-badge packing-lot-badge">{`${_t("lot_no", "Lot No.")} #${lotNumber}`}</span>
                  </div>
                  {(isToday || !daySeparationData) && (
                    <Button size="xs" label={daySeparationData ? _t("edit_data", "Edit Data") : _t("add_data", "Add Data")} icon={PlusSvg} onClick={handleDaySeparationAdd} />
                  )}
                </div>
                <div className="p-3">
                  <Row>
                    {daySeparationLogData.length > 0 ? daySeparationLogData.map((data, index) => (
                      <Row key={index}>
                        {data.map((item, itemIndex) => (
                          <Col lg={6} key={`${index}-${itemIndex}`}>
                            <InfoCard label={item.label} value={item.value} />
                          </Col>
                        ))}
                      </Row>
                    )) : <NoDataFound />}
                  </Row>
                </div>
              </div>
            </Col>
            <Col lg={6}>
              <div className="dash_card h-100">
                <div className="d-flex justify-content-between align-items-center dash_header">
                  <div className="packing-card-header-content">
                    <h3 className="mb-0">{_t("night_separation", "Shift 2 Seperating")}</h3>
                    <span className="shift-badge packing-lot-badge">{`${_t("lot_no", "Lot No.")} #${lotNumber}`}</span>
                  </div>
                  {(isToday || !nightSeparationData) && (
                    <Button size="xs" label={nightSeparationData ? _t("edit_data", "Edit Data") : _t("add_data", "Add Data")} icon={PlusSvg} onClick={handleNightSeparationAdd} />
                  )}
                </div>
                <div className="p-3">
                  <Row>
                    {nightSeparationLogData.length > 0 ? nightSeparationLogData.map((data, index) => (
                      <Row key={index}>
                        {data.map((item, itemIndex) => (
                          <Col lg={6} key={`${index}-${itemIndex}`}>
                            <InfoCard label={item.label} value={item.value} />
                          </Col>
                        ))}
                      </Row>
                    )) : <NoDataFound />}
                  </Row>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Packing Band Log */}
      <div className="packing-band-log-section">
        <h6 className="packing-band-log-title">{_t("packing_band_log", "Packing Band Log")}</h6>
        <div className="gap-2 overviewcard">
          <Row>
            <Col lg={6}>
              <div className="band-card-container">
                <div className="band-card-header">
                  <div className="packing-card-header-content">
                    <h3 className="band-card-title">{_t("night_shift", "Night Shift")}</h3>
                    <span className="shift-badge packing-lot-badge">{`${_t("lot_no", "Lot No.")} #${lotNumber}`}</span>
                  </div>
                  {(isToday || !hasNightBandRecord) && (
                    <Button size="xs" label={hasNightBandRecord ? _t("edit_data", "Edit") : _t("add_data", "Add")} icon={PlusSvg} onClick={handleNightBandAdd} className="band-header-button" />
                  )}
                </div>
                <div className="band-card-body">
                  {nightBandLogData.length > 0 ? nightBandLogData.map((data, index) => (
                    <div key={index} className="mb-2">
                      <div className="band-info-grid">
                        {data.map((item, itemIndex) => (
                          <div key={`${index}-${itemIndex}`} className="band-info-item">
                            <span className="band-info-label">{item.label}</span>
                            <span className="band-info-value">{item.value}</span>
                            {item.subvalue && <span className="band-info-subvalue">{item.subvalue}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )) : <NoDataFound />}
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
};

export default Turn2;
